import { Link } from "@tanstack/react-router"
import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { formatDate } from "lopermut"
import { CircleDashed, ExternalLink, Pencil, RefreshCcw } from "lucide-react"
import { useGetBackofficeScheduledReportsQuery } from "modules/backoffice/hooks/scheduledReports/useGetBackofficeScheduledReportsQuery"
import { useEnqueueBackofficeScheduledReportsMutation } from "modules/backoffice/hooks/scheduledReports/useEnqueueBackofficeScheduledReportsMutation"
import { Pulse } from "modules/shared/components/Miscelanea/Pulse"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  BackofficeScheduledReportsResponseDataSchema,
  BackofficeScheduledReportsSortFieldsEnum,
} from "schema"
import { toast } from "sonner"
import { z } from "zod"

const SLOW_DURATION_MS = 60_000

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof BackofficeScheduledReportsResponseDataSchema>[number]
>()

const formatDuration = (durationMs: number) => {
  const totalSeconds = Math.round(durationMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  if (minutes === 0) return `${seconds}s`
  if (seconds === 0) return `${minutes}m`
  return `${minutes}m ${seconds}s`
}

type BackofficeScheduledReportsQueryReturn = ReturnType<
  typeof useGetBackofficeScheduledReportsQuery
>

type Props = {
  scheduledReports: BackofficeScheduledReportsQueryReturn["data"]
  isPending: BackofficeScheduledReportsQueryReturn["isPending"]
  fields: BackofficeScheduledReportsSortFieldsEnum[]
  onSort: (field: BackofficeScheduledReportsSortFieldsEnum) => void
  search: {
    field: BackofficeScheduledReportsSortFieldsEnum
    order: "asc" | "desc"
  }
}

export const BackofficeScheduledReportsTable = ({
  scheduledReports,
  isPending,
  fields,
  onSort,
  search,
}: Props) => {
  const { t } = useTranslation()
  const { mutateAsync, isPending: enqueueIsPending } =
    useEnqueueBackofficeScheduledReportsMutation()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          id: "isActive",
          cell: (props) => {
            const isActive = props.row.original.isActive

            return isActive ? (
              <Pulse className="fill-success" />
            ) : (
              <CircleDashed className="text-warning" size={12} />
            )
          },
          meta: {
            className: "w-6",
          },
        }),
        columnHelper.accessor("id", {
          header: t("backoffice.scheduledReports.table.id"),
        }),
        columnHelper.accessor("alias", {
          header: t("backoffice.scheduledReports.table.alias"),
        }),
        columnHelper.accessor("frequency", {
          header: t("backoffice.scheduledReports.table.frequency"),
        }),
        columnHelper.accessor("lastRunAt", {
          header: t("backoffice.scheduledReports.table.lastRunAt"),
          cell: (props) => {
            const date = props.getValue()
            if (!date) return "—"

            return <DateDisplay date={date} />
          },
        }),
        columnHelper.display({
          id: "lastDurationMs",
          header: t("backoffice.scheduledReports.table.duration"),
          cell: ({ row }) => {
            const duration = row.original.lastDurationMs
            if (duration === null) return "—"

            const formattedDuration = formatDuration(duration)
            if (duration < SLOW_DURATION_MS) return formattedDuration

            return (
              <Tooltip>
                <TooltipTrigger asChild slick>
                  <div className="w-fit text-destructive">{formattedDuration}</div>
                </TooltipTrigger>
                <TooltipContent>
                  {t("backoffice.scheduledReports.table.durationTooltip", {
                    duration: formattedDuration,
                    threshold: formatDuration(SLOW_DURATION_MS),
                  })}
                </TooltipContent>
              </Tooltip>
            )
          },
        }),
        columnHelper.display({
          id: "lastFailedAt",
          header: t("backoffice.scheduledReports.table.lastFailedAt"),
          cell: ({ row }) => {
            const date = row.original.lastFailedAt
            if (!date) return "—"

            return (
              <Tooltip>
                <TooltipTrigger asChild slick>
                  <div className="w-fit text-destructive">
                    <DateDisplay date={date} format="absolute" />
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="left"
                  className="max-w-lg whitespace-pre-wrap break-words text-left"
                >
                  {t("backoffice.scheduledReports.table.failedTooltip", {
                    date: formatDate(date, "Pp"),
                    reason: row.original.lastFailedReason ?? "—",
                  })}
                </TooltipContent>
              </Tooltip>
            )
          },
        }),
        columnHelper.display({
          id: "actions",
          cell: (props) => {
            const { id, spreadsheetId } = props.row.original

            return (
              <div className="flex gap-2 justify-end">
                <Button
                  size="icon"
                  variant={"testing"}
                  onClick={async () => {
                    await mutateAsync(id)
                    toast.success(t("backoffice.scheduledReports.enqueuedSuccessfully"))
                  }}
                  disabled={enqueueIsPending}
                >
                  <RefreshCcw />
                </Button>
                <Button asChild variant="outline" size="icon">
                  <a
                    href={`https://docs.google.com/spreadsheets/d/${encodeURIComponent(
                      spreadsheetId,
                    )}/edit`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t("backoffice.scheduledReports.table.openSpreadsheet")}
                  >
                    <ExternalLink />
                  </a>
                </Button>
                <Link to="/backoffice/scheduledReports/$id" params={{ id }}>
                  <Button variant="outline" size="icon">
                    <Pencil />
                  </Button>
                </Link>
              </div>
            )
          },
          meta: {
            align: "right",
            className: "max-w-[60px]",
          },
        }),
      ]),
    [t, mutateAsync, enqueueIsPending],
  )

  const table = useTable({
    data: scheduledReports ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || scheduledReports === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (scheduledReports.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("backoffice.customers.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.original.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<
      Features,
      z.infer<typeof BackofficeScheduledReportsResponseDataSchema>[number],
      unknown
    >,
  ) {
    if (!fields.includes(header.id as BackofficeScheduledReportsSortFieldsEnum)) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as BackofficeScheduledReportsSortFieldsEnum),
    }
  }

  return (
    <>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  sortable={buildSortable(header)}
                  {...header.column.columnDef.meta}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{renderContent()}</TableBody>
      </Table>
    </>
  )
}
